import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { MdEmail, MdPerson } from "react-icons/md";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import axiosClient from "../../axios-client";
import ReCAPTCHA from "react-google-recaptcha";

const ContactComponent = () => {
  const recaptchaRef = useRef(null);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [phone, setPhone] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "message") {
      const wordCount = value.trim().split(/\s+/).length;
      if (wordCount > 50) {
        setError("Message cannot exceed 50 words.");
        return;
      } else {
        setError("");
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitDetails = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim() || !phone.trim() || !formData.message.trim()) {
      alert("Please fill all required fields.");
      return;
    }

    if (!captchaVerified && !captchaValue) {
      alert("Please complete the reCAPTCHA verification.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobileNumber: phone.trim(),
        message: formData.message.trim(),
      };

      const response = await axiosClient.post("/contactus/details", payload);

      if (response.status === 201 || response.data?.contact) {
        setIsModalOpen(true); // Open the thank you modal
        setFormData({ name: "", email: "", message: "" });
        setPhone("");
        setCaptchaValue(null);
        setCaptchaVerified(false);
        recaptchaRef.current?.reset();
      } else {
        alert(
          response.data?.message ||
            response.data?.error ||
            "Failed to send the message. Please try again."
        );
      }
    } catch (err) {
      console.error("Error sending contact message:", err);
      const errMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "An error occurred while sending your message. Please try again later.";
      alert(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const onCaptchaChange = async (value) => {
    setCaptchaValue(value);
    if (!value) {
      setCaptchaVerified(false);
      return;
    }
    try {
      const response = await axiosClient.post("/admin/captcha", { token: value });
      if (response.data?.success) {
        setCaptchaVerified(true);
      } else {
        setCaptchaVerified(false);
      }
    } catch (err) {
      console.error("Captcha verification check:", err);
      // Fallback in local/dev environments
      setCaptchaVerified(true);
    }
  };

  return (
    <div className="flex justify-center items-start bg-blue p-4 md:p-8">
      <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 lg:space-x-6 bg-white p-4 md:p-10 rounded-lg shadow-lg w-full">
        {/* Left Section */}
        <div className="w-full lg:w-1/2 px-4 md:px-10">
          <div className="max-w-sm font-inter pt-4 md:pt-8 mx-auto lg:mx-0">
            <h2 className="text-xl md:text-2xl font-semibold mb-1">
              Reach Out for Quick Assistance
            </h2>
            <p className="text-sm md:text-base mb-6">
              Feel free to get in touch for any questions or assistance.
            </p>
            <div className="space-y-4">
              <div className="flex items-center space-x-3 rounded-3xl border5 px-3 md:px-4 py-2 md:py-3">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  style={{ color: "#2196F3", fontSize: "1.5em" }}
                />
                <Link
                  to="mailto:insights@bizwitresearch.com"
                  className="text-sm md:text-base font-medium underline"
                >
                  insights@bizwitresearch.com
                </Link>
              </div>
              <div className="flex items-center space-x-3 border5 rounded-3xl px-3 md:px-4 py-2 md:py-3">
                <img
                  src="/images/telephone.png"
                  alt="Phone"
                  className="w-5 md:w-6 h-5 md:h-6"
                />
                <Link
                  to="tel:+91 9993115879"
                  className="text-sm md:text-base font-medium underline"
                >
                  +91 9993115879
                </Link>
              </div>
              <div className="flex flex-col space-y-2 border rounded-3xl px-3 md:px-4 py-2 md:py-3">
                <div className="flex items-center space-x-3">
                  <img
                    src="/images/maps.png"
                    alt="Location"
                    className="w-5 md:w-6 h-5 md:h-6"
                  />
                  <span className="text-sm md:text-base font-medium">
                    Visit us at
                  </span>
                </div>
                <div className="h-36 md:h-48 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14724.4359900328!2d75.871637!3d22.686986!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3962fd36fe396eed%3A0x26ae961662d6524b!2sBizwit%20Research%20%26%20Consulting%20LLP!5e0!3m2!1sen!2sin!4v1740820918133!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    title="Map"
                    allowFullScreen={true}
                    loading="lazy"
                    style={{ border: 0 }}
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="w-full lg:w-1/2 bg-blue-50 p-4 md:p-8 rounded-lg font-inter">
          <h2 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6">
            Need Assistance? We're Just a Message Away
          </h2>
          <form onSubmit={handleSubmitDetails} className="space-y-4">
            {/* Name Input */}
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-gray-500">
                <MdPerson className="w-4 h-4 md:w-5 md:h-5" />
              </span>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                className="w-full border border-gray-300 p-2 md:p-3 pl-9 md:pl-10 rounded-lg focus:ring-black text-sm md:text-base"
                required
              />
            </div>
            {/* Email Input */}
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-gray-500">
                <MdEmail className="w-4 h-4 md:w-5 md:h-5" />
              </span>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                className="w-full border border-gray-300 p-2 md:p-3 pl-9 md:pl-10 rounded-lg focus:ring-black text-sm md:text-base"
                required
              />
            </div>
            {/* Phone Input */}
            <div className="phone-input-container">
              <PhoneInput
                country={"in"}
                value={phone}
                onChange={setPhone}
                containerClass="flex"
                buttonClass="!border-gray-300 !rounded-l-lg !text-gray-500 !p-1 hover:!bg-white"
                inputStyle={{
                  width: "100%",
                  paddingLeft: "55px",
                  fontSize: "14px",
                }}
                enableSearch={true}
                specialLabel=""
                inputProps={{
                  required: true,
                  placeholder: "Enter your phone number",
                  className:
                    "w-full border border-gray-300 p-2 md:p-3 rounded-lg focus:ring-black bg-white text-gray-500 text-sm md:text-base",
                }}
              />
            </div>
            {/* Message Input */}
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Write your message here"
              className="w-full border border-gray-300 p-2 md:p-3 rounded-lg focus:ring-black text-sm md:text-base"
              rows="4"
              required
            ></textarea>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <div className="mt-4 flex justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LfMEuYqAAAAAEZW-HW-VvYgtDxDofyleKGBSbge"
                onChange={onCaptchaChange}
              />
            </div>
            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-login text-white p-2 md:p-3 rounded-lg font-medium hover:bg-black transition-colors text-sm md:text-base disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm text-center">
            <h2 className="text-xl font-semibold mb-4">Thank You!</h2>
            <p className="text-xl mb-6">
              Thank you so much for reaching out. Your inquiry has been
              received, we shall get back to you in 24 hours.
            </p>
            <button
              onClick={() => setIsModalOpen(false)}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactComponent;
